import React, { useEffect, useMemo, useState, useRef, memo } from "react";
import {
	FaCheck,
	FaEraser,
	FaExclamation,
	FaFloppyDisk,
	FaPenToSquare,
	FaUser,
	FaUserXmark,
} from "react-icons/fa6";
import "./user-profile-sidemodal.style.scss";

// for filter form handle
import { useFormik } from "formik";
import * as yup from "yup";

// for date purpose
import moment from "moment";

// components
import StructuredSideModal from "../structured-side-modal/structured-side-modal.component";
import DialogModal from "../dialog-modal/dialog-modal.component";
import { Button } from "../ui/button";
import SelectBox from "../form-inputs/select-box.component";
import TextField from "../form-inputs/text-field.component";
import DisplayField, { getOptionLabel } from "../form-inputs/display-field.component";
import FormError from "../form-error/form-error.component";
import SuccessDisplay from "../success-display/success-display.component";
import InlineNotice from "../inline-notice/inline-notice.component";
import EmployeeLoginManager from "./employee-login-manager.component";
import EmployeeStatusToggle from "./employee-status-toggle.component";
import EmployeeRoleManager from "./employee-role-manager.component";

// global strings and functions
import strings from "../../global/strings";
import { PHONE_REGEX, PHONE_REGEX_MESSAGE } from "../../global/phone.const";
import {
	fromAdToBs,
	isEmptyObject,
	rupeeConverter,
} from "../../global/function";
import { queryClient } from "../../global/query-client";
import {
	Employee,
	EmployeePayload,
	EmployeePermissions,
	employeesQueryKey,
	useUpdateEmployee,
	useDeleteEmployee,
	useEmployeeDetails,
} from "../../queries/employee.queries";
import { useUploadImage } from "../../queries/image.queries";

import { useSession } from "../../queries/auth.queries";

interface EmployeeProfileProps {
	isEmployeeModalVisible: boolean;
	onEmployeeModalClose: () => void;
	employee: Partial<Employee>;
	currentPageNum?: number;
	empPermission?: EmployeePermissions;
	// Opens directly in edit mode — used by the "Complete Profile" handoff from
	// add-employee-wizard.component.tsx right after a successful create/link,
	// so the user lands straight into filling in the fields the quick-create
	// step deferred, instead of having to click Edit themselves.
	startEditable?: boolean;
}

// The formik values object ends up holding the full Employee row once
// formik.resetForm({values: {...initialValues, ...displayEmployee}}) merges
// the loaded employee in (see the effect below). Login credentials
// (username/password/split/merge) live entirely in EmployeeLoginManager now,
// and role changes in EmployeeRoleManager — neither is part of this form, so
// user_role is deliberately never set here (see buildProfileSnapshotPayload's
// comment for why: sending it unchanged would still trip updateUser's
// owner-only rank check for a non-owner editor doing an unrelated field
// edit). EmployeePayload already covers this shape (see employee.queries.ts).
// Edit-only: new employees are created via add-employee-wizard.component.tsx
// (addLogin's link/create paths).
type EmployeeFormValues = EmployeePayload;

const employeeSchema = yup.object().shape({
	first_name: yup
		.string()
		.nullable()
		.required("Employee First Name is required"),
	last_name: yup
		.string()
		.nullable()
		.required("Employee Last Name is required"),
	user_mobile: yup
		.string()
		.matches(PHONE_REGEX, PHONE_REGEX_MESSAGE)
		.nullable()
		.required("Employee Mobile is required"),
	user_email: yup
		.string()
		.email("Please enter valid email")
		.nullable()
		.required("Employee email is required"),
	user_gender: yup.string().required("Select employee gender"),
	user_address: yup.string().required("Please enter employee address"),
	user_city: yup.string().required("Select employee city"),
});

function EmployeeProfile({
	isEmployeeModalVisible,
	onEmployeeModalClose,
	employee,
	currentPageNum,
	empPermission,
	startEditable = false,
}: EmployeeProfileProps) {
	const { data: session } = useSession();

	// Self-contained to this modal — no other page reads employee bill
	// details, so this stays off Redux entirely (see useEmployeeDetails).
	// EmployeeLoginManager below calls this same hook independently for its
	// own login_shared/is_login_split/etc — React Query dedupes both calls
	// against the same key, so this isn't a duplicate fetch.
	const { data: employeeDetails, isLoading } = useEmployeeDetails(
		employee?.user_info_id || undefined,
	);

	// The caller-supplied `employee` prop is sometimes just { user_info_id }
	// (add-employee-wizard's "already in company" redirect doesn't have a full
	// row to hand over) — prefer the richer record this component fetches for
	// itself once it lands, falling back to the prop only for the instant
	// first paint before that resolves.
	const displayEmployee: Partial<Employee> =
		employeeDetails?.user ?? employee;

	// getUserById's nested `user` (used above once employeeDetails lands) never
	// carries a company_user back-reference — that endpoint returns the role
	// as a top-level sibling instead, reflecting its "root is CompanyUsers"
	// shape (see EmployeeDetails). So displayEmployee.company_user would
	// silently go from populated (list-click path, before employeeDetails
	// resolves) to undefined (after it resolves) — read from the stable
	// `employee` prop and employeeDetails's own top-level field directly
	// instead, so this never flips once the fetch lands.
	const currentUserRole = employee.company_user?.user_role ?? employeeDetails?.user_role;

	const [userEditable, setUserEditable] = useState(startEditable);
	const [selectedImage, setSelectedImage] = useState<File | null>(null);
	const [imageSizeNotice, setImageSizeNotice] = useState<string | null>(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	const fileInputRef = useRef<HTMLInputElement>(null);

	const updateEmployeeMutation = useUpdateEmployee();
	const deleteEmployeeMutation = useDeleteEmployee();
	const uploadImageMutation = useUploadImage();

	// Brief SuccessDisplay before the modal reverts to view mode.
	const showSuccess =
		updateEmployeeMutation.isSuccess || deleteEmployeeMutation.isSuccess;
	const successTitle = deleteEmployeeMutation.isSuccess
		? "Employee deleted"
		: "Employee updated";
	const successDescription = deleteEmployeeMutation.isSuccess
		? "Employee has been removed successfully."
		: "Employee details updated successfully.";
	const successTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	useEffect(() => {
		return () => {
			if (successTimeoutRef.current)
				clearTimeout(successTimeoutRef.current);
		};
	}, []);

	const formik = useFormik<EmployeeFormValues>({
		validationSchema: employeeSchema,
		initialValues: {
			first_name: "",
			last_name: "",
			user_gender: "",
			user_email: "",
			user_mobile: "",
			user_address: "",
			user_city: "",
			user_country: "Nepal",
		},
		onSubmit: async (employeeData) => {
			delete employeeData.user_image;

			let uploadResult = true;
			if (selectedImage) {
				try {
					await uploadImageMutation.mutateAsync({
						image: selectedImage,
						id: employeeData.user_info_id,
						type: "user",
					});
				} catch {
					uploadResult = false;
				}
			}

			if (!uploadResult) return; // FormError (bound to uploadImageMutation) renders the message

			try {
				await updateEmployeeMutation.mutateAsync({
					employeeData: { ...employeeData },
					page: currentPageNum,
				});
			} catch {
				return; // FormError (bound to updateEmployeeMutation) renders the message
			}

			queryClient.invalidateQueries({ queryKey: employeesQueryKey });

			// in case of loggedin user editing their own profile
			if (employeeData?.user_info_id === session?.user_info_id) {
				queryClient.invalidateQueries({ queryKey: ["session"] });
			}

			successTimeoutRef.current = setTimeout(() => {
				updateEmployeeMutation.reset();
				setUserEditable(false);
			}, 1500);
		},
	});

	const handleUserDeleteConfirm = async (userInfoId: string) => {
		if (deleteEmployeeMutation.isPending) return;
		try {
			await deleteEmployeeMutation.mutateAsync(userInfoId);
		} catch {
			setDeleteConfirmOpen(false);
			return; // FormError (bound to deleteEmployeeMutation) renders the message
		}
		setDeleteConfirmOpen(false);
		queryClient.invalidateQueries({ queryKey: employeesQueryKey });
		successTimeoutRef.current = setTimeout(() => {
			deleteEmployeeMutation.reset();
			onEmployeeModalClose();
		}, 1500);
	};

	const handleEditCancel = () => {
		setUserEditable(false);
		setSelectedImage(null);
		updateEmployeeMutation.reset();
	};

	useEffect(() => {
		// A minimal employee prop (no company_user — see displayEmployee above)
		// has nothing worth prefilling from yet; wait for useEmployeeDetails to
		// hydrate the real record instead of resetting the form to blanks.
		const isMinimal = !employee.company_user;
		if (isMinimal && !employeeDetails) return;
		if (isEmptyObject(displayEmployee)) return;

		// resetForm (not setValues) rebases formik's dirty-comparison baseline
		// to this freshly-loaded employee — otherwise dirty would read true
		// the moment this populates, before the user touches anything
		// (formik.dirty backs the discard-changes guard on StructuredSideModal
		// below).
		formik.resetForm({
			values: {
				...formik.initialValues,
				...displayEmployee,
			},
		});
		// eslint-disable-next-line
	}, [employee, employeeDetails]);

	const _renderEmployeeInfo = () => {
		if (!userEditable) {
			return (
				<div className="user-info-container">
					<h2 className="section-header">Employee Details</h2>
					<div className="input-row-wrapper">
						<DisplayField
							label="First Name"
							value={displayEmployee.first_name}
							valueClassName="cap-display"
						/>
						<DisplayField
							label="Last Name"
							value={displayEmployee.last_name}
							valueClassName="cap-display"
						/>
					</div>
					<div className="input-row-wrapper">
						<DisplayField label="Mobile Number" value={displayEmployee.user_mobile} />
						<DisplayField label="Employee Email" value={displayEmployee.user_email} />
					</div>
					<DisplayField
						label="Employee Gender"
						value={getOptionLabel(strings.gender, displayEmployee.user_gender)}
					/>
					<DisplayField label="Address" value={displayEmployee.user_address} />
					<div className="input-row-wrapper">
						<DisplayField
							label="City"
							value={getOptionLabel(strings.city, displayEmployee.user_city)}
						/>
						<DisplayField label="Country" value={displayEmployee.user_country} />
					</div>
				</div>
			);
		}

		return (
			<div className="user-info-container">
				<h2 className="section-header">Employee Details</h2>
				<div className="input-row-wrapper">
					<TextField
						name="first_name"
						label="First Name"
						placeholder="Enter First Name"
						formik={formik}
					/>
					<TextField
						name="last_name"
						label="Last Name"
						placeholder="Enter Last Name"
						formik={formik}
					/>
				</div>

				<div className="input-row-wrapper">
					<TextField
						name="user_mobile"
						label="Mobile Number"
						placeholder="Enter Mobile Number"
						type="tel"
						formik={formik}
					/>
					<TextField
						name="user_email"
						label="Employee Email"
						placeholder="Enter Email"
						formik={formik}
					/>
				</div>
				<div className="input-wrapper">
					<label className="input-label">Employee Gender</label>
					<SelectBox
						name="user_gender"
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						value={formik.values?.user_gender || ""}
						options={strings.gender}
					/>
					{formik.submitCount > 0 && (
						<div className="validation-error">
							{formik?.errors?.user_gender}
						</div>
					)}
				</div>
				<TextField
					name="user_address"
					label="Address"
					placeholder="Enter Address"
					formik={formik}
				/>
				<div className="input-row-wrapper">
					<div className="input-wrapper">
						<label className="input-label">City</label>
						<SelectBox
							name="user_city"
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							value={formik.values?.user_city || ""}
							options={strings.city}
						/>
						{formik.submitCount > 0 && (
							<div className="validation-error">
								{formik?.errors?.user_city}
							</div>
						)}
					</div>
					<TextField
						name="user_country"
						label="Country"
						placeholder="Enter Country"
						formik={formik}
					/>
				</div>
			</div>
		);
	};

	const _renderEmployeeBills = () => {
		if (isLoading) {
			return <div>Loading....</div>;
		} else {
			if (
				!isEmptyObject(employeeDetails) &&
				Array.isArray(employeeDetails?.bills) &&
				employeeDetails?.bills?.length > 0
			) {
				return (
					<div className="user-bills-container">
						<h2 className="section-header">Recent Bills</h2>
						<div className="bills-table-container">
							<div className="table-grid-container header">
								<div className="table-item-text align-left">
									<b>Invoice #</b>
								</div>
								<div className="table-item-text">
									<b>Invoice Date</b>
								</div>
								<div className="table-item-text align-right">
									<b>Amount</b>
								</div>
							</div>
							{employeeDetails?.bills?.map((bill, i) => (
								<div key={i} className="table-grid-container">
									<div
										className="table-item-text align-left copy"
										onClick={() =>
											navigator.clipboard.writeText(
												bill?.invoice_number?.split(
													"_",
												)?.[2] || "",
											)
										}
									>
										{bill?.invoice_number}
									</div>
									<div className="table-item-text">
										{moment(bill?.invoice_date).format(
											"DD MMMM, YYYY",
										)}
										<span>
											{fromAdToBs(
												bill?.invoice_date,
												"DD MMMM, YYYY",
											)}
										</span>
									</div>
									<div className="table-item-text align-right">
										<div
											className={`bill-price ${
												bill?.bill_void && "void"
											}`}
										>
											<b>Rs. </b>
											{rupeeConverter(bill?.sub_total)}
										</div>
									</div>
								</div>
							))}
						</div>
					</div>
				);
			}
		}
	};

	const handleImageEdit = () => {
		if (userEditable) {
			// Trigger the hidden file input element
			fileInputRef.current?.click();
		}
	};

	const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const selectedFile = e.target.files?.[0];
		// Check the file size
		if (selectedFile && selectedFile.size <= 2048576) {
			setSelectedImage(selectedFile);
		} else {
			// Handle the case where the file size exceeds the limit
			setImageSizeNotice("File size exceeds the limit");
		}
	};

	const imgSrc = useMemo(() => {
		if (selectedImage) {
			return URL.createObjectURL(selectedImage);
		} else if (
			!isEmptyObject(displayEmployee) &&
			displayEmployee.user_image
		) {
			return displayEmployee.user_image;
		} else {
			return "";
		}
	}, [displayEmployee, selectedImage]);

	return (
		<>
			<StructuredSideModal
				open={isEmployeeModalVisible}
				onOpenChange={(open) => {
					if (!open) onEmployeeModalClose();
				}}
				title="Employee"
				width={560}
				isDirty={!showSuccess && formik.dirty}
				onSubmit={formik.handleSubmit}
				actions={
					empPermission?.edit &&
					!showSuccess &&
					(userEditable ? (
						<Button
							variant="destructive"
							size="sm"
							type="button"
							onClick={handleEditCancel}
						>
							<FaEraser /> Cancel
						</Button>
					) : (
						<div className="flex items-center gap-2">
							{employee.user_info_id && (
								<EmployeeStatusToggle
									userInfoId={employee.user_info_id}
									empPermission={empPermission}
								/>
							)}
							{empPermission?.delete &&
								currentUserRole !== "owner" && (
									<Button
										variant="outline"
										size="sm"
										type="button"
										onClick={() =>
											setDeleteConfirmOpen(true)
										}
									>
										<FaUserXmark /> Delete
									</Button>
								)}
							<Button
								size="sm"
								type="button"
								onClick={() => setUserEditable(true)}
							>
								<FaPenToSquare /> Edit
							</Button>
						</div>
					))
				}
				footer={
					userEditable &&
					!showSuccess && (
						<div className="flex justify-end">
							<Button size="sm" type="submit">
								<FaFloppyDisk /> Save
							</Button>
						</div>
					)
				}
			>
				<div className="user-modal-container">
					<div className="modal-body">
						{showSuccess ? (
							<SuccessDisplay
								title={successTitle}
								description={successDescription}
							/>
						) : (
							<>
								<FormError
									isError={
										uploadImageMutation.isError ||
										updateEmployeeMutation.isError ||
										deleteEmployeeMutation.isError
									}
									error={
										uploadImageMutation.error ||
										updateEmployeeMutation.error ||
										deleteEmployeeMutation.error
									}
									fallback="Something went wrong. Please try again."
								/>
								{displayEmployee?.user_id && (
									<div className="user-id">
										# {displayEmployee?.user_id}
									</div>
								)}
								<div
									className={`user-img-container ${
										userEditable && "editable"
									}`}
									onClick={handleImageEdit}
								>
									<div className="item_img">
										{imgSrc ? (
											<img
												crossOrigin="anonymous"
												src={imgSrc}
												alt="user"
											/>
										) : (
											<FaUser size="6em" />
										)}
									</div>
									{userEditable ? (
										<div className="user-img-action">
											<FaPenToSquare />
										</div>
									) : (
										<div
											className={`user-img-action ${
												displayEmployee.user_verified
													? "active"
													: "inactive"
											}`}
										>
											{displayEmployee.user_verified ? (
												<FaCheck />
											) : (
												<FaExclamation />
											)}
										</div>
									)}
								</div>
								<input
									type="file"
									accept="image/*"
									ref={fileInputRef}
									style={{ display: "none" }}
									onChange={handleImageChange}
								/>
								<InlineNotice
									message={imageSizeNotice}
									type="warning"
									onDismiss={() => setImageSizeNotice(null)}
								/>
								{employee.user_info_id && (
									<EmployeeRoleManager
										userInfoId={employee.user_info_id}
										empPermission={empPermission}
										currentPageNum={currentPageNum}
									/>
								)}
								<div className="divider" />
								{_renderEmployeeInfo()}
								{!userEditable && (
									<>
										<div className="divider" />
										{employee.user_info_id && (
											<EmployeeLoginManager
												userInfoId={employee.user_info_id}
												empPermission={empPermission}
												currentPageNum={currentPageNum}
											/>
										)}
										<div className="divider" />
										{_renderEmployeeBills()}
									</>
								)}
							</>
						)}
					</div>
				</div>
			</StructuredSideModal>
			<DialogModal
				open={deleteConfirmOpen}
				onOpenChange={setDeleteConfirmOpen}
				title="Delete Employee?"
				description="Are you sure you want to delete the user?"
				footer={
					<>
						<Button
							variant="outline"
							onClick={() => setDeleteConfirmOpen(false)}
							disabled={deleteEmployeeMutation.isPending}
						>
							Cancel
						</Button>
						<Button
							onClick={() =>
								employee.user_info_id &&
								handleUserDeleteConfirm(employee.user_info_id)
							}
							disabled={deleteEmployeeMutation.isPending}
						>
							{deleteEmployeeMutation.isPending
								? "Deleting..."
								: "Delete"}
						</Button>
					</>
				}
			/>
		</>
	);
}

export default memo(EmployeeProfile);
